import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { AccountFeatureAttributes } from '../../../generated/internal.graphql';
import type { VPResult } from '../../VPResult';
import { type InternalApiError, toApiErrors } from '../errors';
import { COLLECTION_METADATA_FIELDS, type CollectionArguments } from '../../collection/Api';
import * as GqlCollection from '../../collection/GqlCollection';
import * as FeatureAccount from './FeatureAccount';
import * as FeatureFlag from './FeatureFlag';
import * as FeatureFlagHistoryEntry from './FeatureFlagHistoryEntry';
import * as FeatureInventory from './FeatureInventory';
import {
  INTERNAL_FEATURE_ACCOUNT_FIELDS,
  INTERNAL_FEATURE_FLAG_FIELDS,
  INTERNAL_FEATURE_FLAG_HISTORY_ENTRY_FIELDS,
  INTERNAL_FEATURE_INVENTORY_FIELDS,
} from './fragments';
import { ACCOUNT_AGNOSTIC, type AccountContext, forAccount } from '../../accountContext';

/**
 * Feature flags read and toggled by flag rather than by account, on the internal (VP staff) schema
 * per ADR-009. The gate's 401 is classified here, so nothing throws, and everything reads
 * `network-only`: adoption counts going stale is the one thing the screens exist to get right. The
 * reads are account-agnostic; `setAccountFeature` alone names the account it writes to.
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

const INTERNAL_FEATURE_INVENTORY_QUERY = gql`
  query InternalFeatureInventory {
    featureInventory {
      ...InternalFeatureInventoryFields
    }
  }
  ${INTERNAL_FEATURE_INVENTORY_FIELDS}
`;

const INTERNAL_ACCOUNTS_BY_FEATURE_QUERY = gql`
  query InternalAccountsByFeature($feature: String!, $holding: Boolean, $name: String, $pagination: PaginationArgument) {
    accountsByFeature(feature: $feature, holding: $holding, name: $name, pagination: $pagination) {
      collection {
        ...InternalFeatureAccountFields
      }
      metadata {
        ...CollectionMetadataFields
      }
    }
  }
  ${INTERNAL_FEATURE_ACCOUNT_FIELDS}
  ${COLLECTION_METADATA_FIELDS}
`;

const INTERNAL_FEATURE_FLAG_HISTORY_QUERY = gql`
  query InternalFeatureFlagHistory($accountId: Int, $feature: String, $pagination: PaginationArgument) {
    featureFlagHistory(accountId: $accountId, feature: $feature, pagination: $pagination) {
      collection {
        ...InternalFeatureFlagHistoryEntryFields
      }
      metadata {
        ...CollectionMetadataFields
      }
    }
  }
  ${INTERNAL_FEATURE_FLAG_HISTORY_ENTRY_FIELDS}
  ${COLLECTION_METADATA_FIELDS}
`;

const INTERNAL_FEATURES_QUERY = gql`
  query InternalFeatures {
    features {
      ...InternalFeatureFlagFields
    }
  }
  ${INTERNAL_FEATURE_FLAG_FIELDS}
`;

const INTERNAL_SET_FEATURE_FLAGS_MUTATION = gql`
  mutation InternalSetFeatureFlags($input: SetAccountFeaturesInput!) {
    setFeatureFlags(input: $input) {
      success
    }
  }
`;

export const fetchFeatureInventory = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<FeatureInventory.FeatureInventory[], InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_FEATURE_INVENTORY_QUERY,
      fetchPolicy: 'network-only',
      context: ACCOUNT_AGNOSTIC,
    });

    // Non-null on the schema: an empty list here would render "no features" over a failed read.
    if (!data?.featureInventory) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the feature inventory.' }] };
    }

    return { success: true, data: data.featureInventory.map(FeatureInventory.fromGql) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** `holding` is a tri-state — unset places no restriction; never collapse `false` into it. */
export const fetchAccountsByFeature = async (
  client: ApolloClient<NormalizedCacheObject>,
  {
    feature,
    holding,
    name,
    pagination,
  }: {
    feature: string;
    holding?: boolean;
    name?: string;
    pagination?: CollectionArguments;
  },
): Promise<VPResult<GqlCollection.GqlCollection<FeatureAccount.FeatureAccount>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ACCOUNTS_BY_FEATURE_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        feature,
        holding,
        name,
        pagination: { page: pagination?.page, perPage: pagination?.limit },
      },
      context: ACCOUNT_AGNOSTIC,
    });

    if (!data?.accountsByFeature) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the clients for this feature.' }] };
    }

    return {
      success: true,
      data: GqlCollection.fromGql(data.accountsByFeature, FeatureAccount.fromGqlCollection),
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** Newest first. With neither filter it lists every change on the platform, so callers pass one. */
export const fetchFeatureFlagHistory = async (
  client: ApolloClient<NormalizedCacheObject>,
  {
    accountId,
    feature,
    pagination,
  }: {
    accountId?: number;
    feature?: string;
    pagination?: CollectionArguments;
  },
): Promise<VPResult<GqlCollection.GqlCollection<FeatureFlagHistoryEntry.FeatureFlagHistoryEntry>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_FEATURE_FLAG_HISTORY_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        accountId,
        feature,
        pagination: { page: pagination?.page, perPage: pagination?.limit },
      },
      context: ACCOUNT_AGNOSTIC,
    });

    if (!data?.featureFlagHistory) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the feature-flag history.' }] };
    }

    return {
      success: true,
      data: GqlCollection.fromGql(data.featureFlagHistory, FeatureFlagHistoryEntry.fromGqlCollection),
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** The declaration list, without the adoption counts `fetchFeatureInventory` carries. */
export const fetchFeatureCatalog = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<FeatureFlag.FeatureFlag[], InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_FEATURES_QUERY,
      fetchPolicy: 'network-only',
      context: ACCOUNT_AGNOSTIC,
    });

    // Non-null on the schema: an empty list here would render "no flags are declared" over a
    // read that simply failed, and invite a grant on a flag the client already holds.
    if (!data?.features) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the feature catalog.' }] };
    }

    return { success: true, data: data.features.map(FeatureFlag.fromGql) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export type SetAccountFeatureInput = { accountId: number; feature: string; enabled: boolean };
export type SetDefaultFeatureInput = { feature: string; enabled: boolean };
export type SetFeatureForAllInput = { feature: string; enabled: boolean };

const runSetFeatureFlags = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountFeatures: AccountFeatureAttributes[],
  context: AccountContext,
): Promise<MutationResult> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SET_FEATURE_FLAGS_MUTATION,
      variables: { input: { accountFeatures } },
      fetchPolicy: 'network-only',
      context,
    });

    // `SetAccountFeaturesPayload` carries no `errors` field, so a refusal arrives as nothing but
    // `success: false` and the caller still needs something to show.
    if (!data?.setFeatureFlags?.success) {
      return { success: false, errors: [{ path: '', message: 'Failed to update the feature flag.' }] };
    }

    return { success: true, errors: [] };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

// Names the account explicitly: staff toggling a flag are not a member of the account they target.
export const setAccountFeature = (client: ApolloClient<NormalizedCacheObject>, { accountId, feature, enabled }: SetAccountFeatureInput) =>
  runSetFeatureFlags(client, [{ accountId, feature, enabled }], forAccount(accountId));

// Omitting `accountId` writes the `account_id IS NULL` row — the default for new accounts.
export const setDefaultFeature = (client: ApolloClient<NormalizedCacheObject>, { feature, enabled }: SetDefaultFeatureInput) =>
  runSetFeatureFlags(client, [{ feature, enabled }], ACCOUNT_AGNOSTIC);

export const setFeatureForAllAccounts = (client: ApolloClient<NormalizedCacheObject>, { feature, enabled }: SetFeatureForAllInput) =>
  runSetFeatureFlags(client, [{ applyToAll: true, feature, enabled }], ACCOUNT_AGNOSTIC);
